/**
 * 首页「立即生成」表单草稿 — H5 与原生端缓存交互（均为 H5 提供的 JS 方法）
 *
 * 原生 → H5：window.saveDraftCache()
 *   断网前原生调用，H5 返回当前表单 JSON 字符串，格式：{"data":"<表单字段 JSON 字符串>"}
 *
 * 原生 → H5：window.restoreDraftCache(json)
 *   断网重连 / 刷新页面后，原生将之前缓存的 JSON 回传，格式同上
 *
 * H5 → 原生：fetchDraftCacheFromNative()
 *   页面进入时主动读取原生缓存，iOS: ydppt://getDraftCache，Android: JsAndroid.getDraftCache()
 *
 * 原生 → H5：window.switchTab(tab)
 *   切 Tab 时通知 H5 同步草稿（tab 如 home | template | production | member）
 */

import { useAppStore } from '@/store/app'
import { useFileStore } from '@/store/file'

export interface CreateDraftCachePayload {
  /** PPT 主题输入 */
  inputText: string
  /** 页数选项，如「5-10页」「20页以内」 */
  pageCount: string
  /** 语言，如 zh / en */
  language: string
  /** 受众 */
  audiences: string
  /** 文本量，如「详细」 */
  textVolume: string
  /** 场景 */
  scene: string
  /** 参考文档的解析内容（旧版流程可能为文件 URL） */
  fujian?: string
  /** 上传文档文件名，用于 UI 展示 */
  fileName?: string
  /** 上传成功的参考图片 URL */
  pics?: string[]
}

/** 原生与 H5 约定的信封格式 */
export interface DraftCacheEnvelope {
  data: string
}

type GetDraftHandler = () => CreateDraftCachePayload
type RestoreHandler = (payload: CreateDraftCachePayload) => void

/** 跨 WebView / 原生 Tab 共享的草稿 localStorage key */
export const OUTLINE_DRAFT_STORAGE_KEY = 'ydppt_outline_draft'
const OUTLINE_DRAFT_COOKIE_KEY = 'ydppt_outline_draft_c'

/** 将草稿编码为 URL 安全的 base64（避免 JSON 特殊字符被微信 query 截断/破坏） */
export function encodeOutlineDraftForTransport(payload: CreateDraftCachePayload): string {
  const json = JSON.stringify(payload)
  const b64 = btoa(unescape(encodeURIComponent(json)))
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** 解析 URL / 小程序传来的草稿（支持 raw JSON 与 base64） */
export function decodeOutlineDraftFromTransport(raw: string | null | undefined): CreateDraftCachePayload | null {
  if (!raw) return null
  const text = String(raw).trim()
  if (!text) return null

  const tryParse = (value: string): CreateDraftCachePayload | null => {
    try {
      const parsed = JSON.parse(value)
      if (isDraftPayload(parsed)) return parsed
      // wrapPayload 信封
      return unwrapPayload(parsed)
    } catch {
      return null
    }
  }

  // 1) 直接 JSON
  const asJson = tryParse(text)
  if (asJson) return asJson

  // 2) decodeURIComponent 后再 JSON
  try {
    const decoded = decodeURIComponent(text)
    const asDecoded = tryParse(decoded)
    if (asDecoded) return asDecoded
  } catch {
    /* ignore */
  }

  // 3) base64url / base64
  try {
    const b64 = text.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64 + '==='.slice((b64.length + 3) % 4)
    const json = decodeURIComponent(escape(atob(pad)))
    const asB64 = tryParse(json)
    if (asB64) return asB64
  } catch {
    /* ignore */
  }

  return null
}

export function writeOutlineDraftToCookie(payload: CreateDraftCachePayload): void {
  try {
    const value = encodeURIComponent(encodeOutlineDraftForTransport(payload))
    document.cookie = `${OUTLINE_DRAFT_COOKIE_KEY}=${value}; path=/; max-age=600; SameSite=Lax`
  } catch {
    /* ignore */
  }
}

export function readOutlineDraftFromCookie(): CreateDraftCachePayload | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${OUTLINE_DRAFT_COOKIE_KEY}=([^;]*)`))
    if (!match?.[1]) return null
    return decodeOutlineDraftFromTransport(decodeURIComponent(match[1]))
  } catch {
    return null
  }
}

/**
 * 页面进入时解析草稿：URL query > cookie > localStorage
 */
export function resolveOutlineDraftForCreatePage(queryRaw?: string | null): CreateDraftCachePayload | null {
  const fromQuery = decodeOutlineDraftFromTransport(queryRaw)
  if (hasDraftContent(fromQuery)) return fromQuery

  const fromCookie = readOutlineDraftFromCookie()
  if (hasDraftContent(fromCookie)) return fromCookie

  const fromStorage = loadOutlineDraftFromStorage()
  if (hasDraftContent(fromStorage)) return fromStorage

  return null
}

let getDraftHandler: GetDraftHandler | null = null
let restoreHandler: RestoreHandler | null = null
let pendingRestore: CreateDraftCachePayload | null = null

const wrapPayload = (payload: CreateDraftCachePayload): string =>
  JSON.stringify({ data: JSON.stringify(payload) })

const isDraftPayload = (value: unknown): value is CreateDraftCachePayload =>
  !!value && typeof value === 'object' && 'inputText' in value

const parseJsonString = (raw: string): unknown => {
  const trimmed = raw.trim()
  if (!trimmed) return null
  return JSON.parse(trimmed)
}

const unwrapPayload = (envelope: unknown): CreateDraftCachePayload | null => {
  if (!envelope || typeof envelope !== 'object') return null

  const record = envelope as Record<string, unknown>
  const inner = record.data

  if (isDraftPayload(inner)) return inner

  if (typeof inner === 'string') {
    if (!inner.trim()) return null
    try {
      const parsed = parseJsonString(inner)
      if (isDraftPayload(parsed)) return parsed
      if (typeof parsed === 'string') {
        const nested = parseJsonString(parsed)
        return isDraftPayload(nested) ? nested : null
      }
    } catch {
      return null
    }
  }

  return isDraftPayload(record) ? record : null
}

const parseEnvelope = (raw: unknown): CreateDraftCachePayload | null => {
  if (raw == null || raw === '') return null

  
  // 原生 evaluateJavascript 可能直接传入对象：restoreDraftCache({"data":"..."})
  if (typeof raw !== 'string') {
    return unwrapPayload(raw)
  }

  try {
    let envelope: unknown = parseJsonString(raw)

    // 兼容双重 stringify：外层 parse 后仍是字符串
    if (typeof envelope === 'string') {
      envelope = parseJsonString(envelope)
    }

    return unwrapPayload(envelope)
  } catch {
    return null
  }
}

/**
 * 注册获取草稿数据的函数（MobileHome 挂载时调用）
 */
export function registerSaveDraftCacheGetter(handler: GetDraftHandler): void {
  getDraftHandler = handler
}

export function unregisterSaveDraftCacheGetter(): void {
  getDraftHandler = null
}

/**
 * 注册恢复草稿时的处理函数（MobileHome 挂载时调用）
 */
export function registerRestoreDraftCache(handler: RestoreHandler): void {
  restoreHandler = handler
  if (pendingRestore) {
    commitOutlineDraft(pendingRestore)
    pendingRestore = null
  }
}

export function unregisterRestoreDraftCache(): void {
  restoreHandler = null
}

/**
 * 原生断网前调用：获取当前「立即生成」表单草稿
 * @returns JSON 字符串，格式 {"data":"<CreateDraftCachePayload 的 JSON 字符串>"}
 */
export function saveDraftCache(): string {
  const payload = getDraftHandler?.() ?? {
    inputText: '',
    pageCount: '',
    language: '',
    audiences: '',
    textVolume: '',
    scene: '',
  }
  if (getDraftHandler) {
    saveOutlineDraftToStorage(payload)
  }
  return wrapPayload(payload)
}

const hasDraftContent = (payload: CreateDraftCachePayload | null | undefined): boolean =>
  !!payload && !!(payload.inputText?.trim() || payload.fujian || payload.pics?.length)

/** 写入 localStorage，供其他原生 Tab 的 WebView 读取 */
export function saveOutlineDraftToStorage(payload: CreateDraftCachePayload): void {
  try {
    localStorage.setItem(OUTLINE_DRAFT_STORAGE_KEY, wrapPayload(payload))
  } catch {
    /* ignore */
  }
}

/** 从 localStorage 读取跨 Tab 草稿 */
export function loadOutlineDraftFromStorage(): CreateDraftCachePayload | null {
  try {
    const raw = localStorage.getItem(OUTLINE_DRAFT_STORAGE_KEY)
    return raw ? parseEnvelope(raw) : null
  } catch {
    return null
  }
}

export function clearOutlineDraftFromStorage(): void {
  pendingRestore = null
  try {
    localStorage.removeItem(OUTLINE_DRAFT_STORAGE_KEY)
    document.cookie = `${OUTLINE_DRAFT_COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`
  } catch {
    /* ignore */
  }
}

const commitOutlineDraft = (payload: CreateDraftCachePayload | null) => {
  if (!payload || !hasDraftContent(payload)) return
  saveOutlineDraftToStorage(payload)
  if (restoreHandler) {
    restoreHandler(payload)
    return
  }
  pendingRestore = payload
  applyOutlineDraftGlobally(payload)
}

const pickDraftPayload = (
  nativePayload: CreateDraftCachePayload | null,
  storedPayload: CreateDraftCachePayload | null,
): CreateDraftCachePayload | null => {
  // localStorage 由 commitOutlineDraft 统一写入，跨 Tab 更可靠；
  // 原生 getDraftCache 可能仍是另一个 WebView 断网前的旧数据
  if (hasDraftContent(storedPayload)) return storedPayload
  if (hasDraftContent(nativePayload)) return nativePayload
  return null
}

const DEFAULT_OUTLINE_OPTIONS = {
  pageCount: '20页以内',
  language: 'zh',
  audiences: '',
  textVolume: '详细',
  scene: '',
}

/** 将草稿写入 Pinia（不依赖页面级 restoreHandler，供切 Tab / storage 事件使用） */
export function applyOutlineDraftGlobally(payload: CreateDraftCachePayload): void {
  const appStore = useAppStore()
  const fileStore = useFileStore()

  appStore.outlineParams.inputText = payload.inputText || ''
  appStore.outlineParams.pageCount = payload.pageCount || DEFAULT_OUTLINE_OPTIONS.pageCount
  appStore.outlineParams.language = payload.language || DEFAULT_OUTLINE_OPTIONS.language
  appStore.outlineParams.audiences = payload.audiences ?? DEFAULT_OUTLINE_OPTIONS.audiences
  appStore.outlineParams.textVolume = payload.textVolume || DEFAULT_OUTLINE_OPTIONS.textVolume
  appStore.outlineParams.scene = payload.scene ?? DEFAULT_OUTLINE_OPTIONS.scene
  appStore.outlineParams.fujian = payload.fujian || ''
  appStore.outlineParams.pics = Array.isArray(payload.pics) ? payload.pics.filter(pic => typeof pic === 'string' && pic.length > 0).slice(0, 5) : []

  if (payload.fujian && payload.fileName) {
    fileStore.setUploadedFile({
      name: payload.fileName,
      fujian: payload.fujian,
    })
  } else if (!payload.fujian) {
    fileStore.clearFile()
  }
}

/** 当前 WebView 有输入页挂载时，先把最新草稿写入 localStorage */
export function persistOutlineDraftIfPossible(): void {
  const payload = getDraftHandler?.()
  if (payload) {
    saveOutlineDraftToStorage(payload)
  }
}

const resolveOutlineDraftPayload = (): CreateDraftCachePayload | null => {
  const stored = loadOutlineDraftFromStorage()
  const nativeRaw = fetchDraftCacheFromNative()
  const nativePayload = nativeRaw ? parseEnvelope(nativeRaw) : null
  return pickDraftPayload(nativePayload, stored)
}

/**
 * 原生切 Tab / 跨 WebView storage 变更 / 页面重新可见时同步草稿
 * 只读取并应用，不用当前 WebView 内存覆盖 localStorage（避免另一 Tab 的新数据被冲掉）
 */
export function handleNativeTabSwitch(_tab?: string): void {
  const payload = resolveOutlineDraftPayload()
  commitOutlineDraft(payload)
}

/**
 * 页面进入时恢复草稿：优先原生缓存，其次 localStorage（跨 Tab 同步）
 */
export function restoreOutlineDraftOnEnter(): void {
  handleNativeTabSwitch()
}

const isEmptyDraftCacheResult = (value: string | null | undefined): boolean =>
  value == null || value === '' || value === 'false' || value === 'null'

/**
 * H5 主动从原生读取已缓存的草稿 JSON 字符串
 * @returns 格式 {"data":"<CreateDraftCachePayload 的 JSON 字符串>"}，无缓存时返回 null
 */
export function fetchDraftCacheFromNative(): string | null {
  try {
    if (window.JsAndroid?.getDraftCache) {
      const result = window.JsAndroid.getDraftCache()
      return isEmptyDraftCacheResult(result) ? null : result
    }
    if (window.prompt) {
      const result = window.prompt('ydppt://getDraftCache')
      return isEmptyDraftCacheResult(result) ? null : result
    }
  } catch {
    /* 非 App 环境忽略 */
  }
  return null
}

/**
 * 从原生拉取草稿并恢复表单（页面进入时调用）
 */
export function restoreDraftCacheFromNative(): void {
  restoreOutlineDraftOnEnter()
}

/**
 * 原生断网重连后调用：恢复「立即生成」表单草稿
 * @param json JSON 字符串或对象，格式 {"data":"<之前缓存的 JSON 字符串>"}
 */
export function restoreDraftCache(json: string | DraftCacheEnvelope): void {
  const payload = parseEnvelope(json)
  if (!payload) return
  setTimeout(() => {
    commitOutlineDraft(payload)
  }, 500)
}

/** 挂载到 window，供原生 evaluateJavascript 调用 */
export function installDraftCacheBridge(): void {
  window.saveDraftCache = saveDraftCache
  window.restoreDraftCache = restoreDraftCache
}
